import React, {FC, useContext, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronRight} from "@fortawesome/free-solid-svg-icons";
import {EventsStoredObjects, EventStoredObject} from "../../../@types/response";
import {getEvents} from "../../../store/selectors/live/getEvents";
import {MODALS_TYPES} from "../../../constants/ModalsConstants";
import {ModalsContext} from "../modals/ModalsContext";
import {EventModalProps} from "../../../@types/inputs";
import {SPORT_NAME} from "../../../constants/CommonConstants";
import {getFixtures} from "../../../store/selectors/getFixtures";
import {toEpochMs, toLocalDate} from "../../../store/utils/commonUtils";

type LiveEventsDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    toggleButtonRef?: React.RefObject<HTMLElement | null>;
};

type LiveTreeCompetition = {
    id: number;
    name: string;
    events: EventStoredObject[];
};

type LiveTreeCategory = {
    id: number;
    name: string;
    totalEvents: number;
    competitions: Record<string, LiveTreeCompetition>;
};

type LiveTreeSport = {
    id: number;
    name: string;
    totalEvents: number;
    categories: Record<string, LiveTreeCategory>;
};

function formatEventDate(value: unknown): string {
    const d = toLocalDate(value);
    if (!d) return "--/-- --:--";
    const dd = d.getDate().toString().padStart(2, "0");
    const mm = (d.getMonth() + 1).toString().padStart(2, "0");
    const hh = d.getHours().toString().padStart(2, "0");
    const mi = d.getMinutes().toString().padStart(2, "0");
    return `${dd}/${mm} ${hh}:${mi}`;
}

function mergeLiveEventWithFixture(liveEvent: EventStoredObject, fixture?: EventStoredObject): EventStoredObject {
    return {
        _id: liveEvent._id,
        sportId: fixture?.sportId ?? liveEvent.sportId,
        sport: fixture?.sport ?? liveEvent.sport,
        categoryId: fixture?.categoryId ?? liveEvent.categoryId,
        category: fixture?.category ?? liveEvent.category,
        competitionId: fixture?.competitionId ?? liveEvent.competitionId,
        competition: fixture?.competition ?? liveEvent.competition,
        name: fixture?.name ?? liveEvent.name,
        date: fixture?.date ?? liveEvent.date,
        minute: liveEvent.minute ?? fixture?.minute,
        score: liveEvent.score ?? fixture?.score,
        status: liveEvent.status ?? fixture?.status,
    };
}

const LiveEventRow: FC<{ event: EventStoredObject; onClick: () => void }> = ({event, onClick}) => {
    const minute = event.minute ?? null;
    const score = event.score ?? "-";
    return (
            <li className="px-2 py-1 bg-[#2d3438] hover:bg-white/10 cursor-pointer" onClick={onClick}>
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="text-white truncate 2xl:text-monitorBase text-[0.68rem] font-medium">
                            {event.name}
                        </div>
                        <div className="text-[0.65rem] opacity-80">
                            {formatEventDate(event.date)}
                        </div>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0 text-[0.68rem]">
                        <div className="text-white font-semibold">{score}</div>
                        <div className="opacity-80">{minute == null ? "-" : `${minute}'`}</div>
                    </div>
                </div>
            </li>
    );
};

const CompetitionNode: FC<{ competition: LiveTreeCompetition; onEventClick: (event: EventStoredObject) => void }> = ({
                                                                                                                      competition,
                                                                                                                      onEventClick,
                                                                                                                  }) => {
    const [expanded, setExpanded] = useState(false);
    return (
            <li className="my-1">
                <button className="flex items-center w-full px-2 py-1 hover:bg-white/10"
                        onClick={() => setExpanded(!expanded)}>
                    <FontAwesomeIcon
                            className={"transition-transform duration-500 transform " + (expanded ? "rotate-90" : "rotate-0")}
                            icon={faChevronRight}
                    />
                    <span className="ml-2 truncate text-left uppercase">
                        {competition.name} <span className="text-gray-400">{`(${competition.events.length})`}</span>
                    </span>
                </button>
                {expanded && (
                        <ul className="pl-6">
                            {competition.events
                                    .slice()
                                    .sort((a, b) => (toEpochMs(a.date) ?? 0) - (toEpochMs(b.date) ?? 0))
                                    .map((event) => (
                                            <LiveEventRow key={`live-event-${event._id}`} event={event}
                                                          onClick={() => onEventClick(event)}/>
                                    ))}
                        </ul>
                )}
            </li>
    );
};

const CategoryNode: FC<{ category: LiveTreeCategory; onEventClick: (event: EventStoredObject) => void }> = ({
                                                                                                                category,
                                                                                                                onEventClick,
                                                                                                            }) => {
    const [expanded, setExpanded] = useState(false);
    const competitions = useMemo(() => Object.values(category.competitions).sort((a, b) => a.name.localeCompare(b.name)), [category]);
    return (
            <li className="my-2">
                <button className="flex items-center w-full px-2 py-1 hover:bg-white/10"
                        onClick={() => setExpanded(!expanded)}>
                    <FontAwesomeIcon
                            className={"transition-transform duration-500 transform " + (expanded ? "rotate-90" : "rotate-0")}
                            icon={faChevronRight}
                    />
                    <span className="ml-2 truncate text-left uppercase">
                        {category.name} <span className="text-gray-400">{`(${category.totalEvents})`}</span>
                    </span>
                </button>
                {expanded && (
                        <ul className="pl-4">
                            {competitions.map((competition) => (
                                    <CompetitionNode
                                            key={`live-competition-${competition.id}`}
                                            competition={competition}
                                            onEventClick={onEventClick}
                                    />
                            ))}
                        </ul>
                )}
            </li>
    );
};

const SportNode: FC<{ sport: LiveTreeSport; onEventClick: (event: EventStoredObject) => void }> = ({
                                                                                                       sport,
                                                                                                       onEventClick
                                                                                                   }) => {
    const [expanded, setExpanded] = useState(true);
    const categories = useMemo(() => Object.values(sport.categories).sort((a, b) => a.name.localeCompare(b.name)), [sport]);
    return (
            <li className="my-2">
                <button className="flex items-center w-full px-2 py-1 hover:bg-white/10"
                        onClick={() => setExpanded(!expanded)}>
                    <FontAwesomeIcon
                            className={"transition-transform duration-500 transform " + (expanded ? "rotate-90" : "rotate-0")}
                            icon={faChevronRight}
                    />
                    <span className="ml-2 truncate text-left font-semibold uppercase">
                        {sport.name} <span className="text-gray-400">{`(${sport.totalEvents})`}</span>
                    </span>
                </button>
                {expanded && (
                        <ul className="pl-4">
                            {categories.map((category) => (
                                    <CategoryNode key={`live-category-${category.id}`} category={category}
                                                  onEventClick={onEventClick}/>
                            ))}
                        </ul>
                )}
            </li>
    );
};

export const LiveEventsDrawer: FC<LiveEventsDrawerProps> = ({isOpen}) => {
    const fixtures: EventsStoredObjects = useSelector(getFixtures);
    const events: EventsStoredObjects = useSelector(getEvents);
    const {setModalsDataCallback, setOpenedModalsCallback} = useContext(ModalsContext);

    const tree: LiveTreeSport[] = useMemo(() => {
        const grouped: Record<string, LiveTreeSport> = {};
        Object.values(events).forEach((liveEvent) => {
            const mergedEvent = mergeLiveEventWithFixture(liveEvent, fixtures[liveEvent._id]);
            if (!mergedEvent.sportId || !mergedEvent.categoryId || !mergedEvent.competitionId || !mergedEvent.status) return;

            const sportKey = mergedEvent.sportId.toString();
            const categoryKey = mergedEvent.categoryId.toString();
            const competitionKey = mergedEvent.competitionId.toString();

            if (!grouped[sportKey]) {
                grouped[sportKey] = {
                    id: mergedEvent.sportId,
                    name: SPORT_NAME[sportKey] ?? mergedEvent.sport ?? sportKey,
                    totalEvents: 0,
                    categories: {},
                };
            }

            const sport = grouped[sportKey];
            if (!sport.categories[categoryKey]) {
                sport.categories[categoryKey] = {
                    id: mergedEvent.categoryId,
                    name: mergedEvent.category ?? categoryKey,
                    totalEvents: 0,
                    competitions: {},
                };
            }

            const category = sport.categories[categoryKey];
            if (!category.competitions[competitionKey]) {
                category.competitions[competitionKey] = {
                    id: mergedEvent.competitionId,
                    name: mergedEvent.competition ?? competitionKey,
                    events: [],
                };
            }

            category.competitions[competitionKey].events.push(mergedEvent);
            category.totalEvents += 1;
            sport.totalEvents += 1;
        });

        return Object.values(grouped).sort((a, b) => a.id - b.id);
    }, [events, fixtures]);

    const openEvent = (event: EventStoredObject) => {
        const modalEvent: EventModalProps = {
            id: event._id,
            name: event.name,
            sportId: event.sportId,
            date: event.date,
            minute: event.minute,
            score: event.score,
        };
        setModalsDataCallback(MODALS_TYPES.EVENT_LIVE_DETAILS_MODAL, {event: modalEvent});
        setOpenedModalsCallback(MODALS_TYPES.EVENT_LIVE_DETAILS_MODAL, true);
    };

    return (
            <aside
                    className={
                            " fixed top-14 pb-6 transform -right-80 2xl:w-80 w-56 h-full bg-toolbar-bgMain text-white overflow-auto dropdown-hidden-scrollbar " +
                            "ease-in-out transition-all duration-300 z-30 " +
                            (isOpen ? "-translate-x-80" : "translate-x-0")
                    }
            >
                <ul className="py-2">
                    {tree.length === 0 && <li className="px-3 py-2 opacity-80">No live events</li>}
                    {tree.map((sport) => (
                            <SportNode key={`live-sport-${sport.id}`} sport={sport} onEventClick={openEvent}/>
                    ))}
                </ul>
            </aside>
    );
};